import React, { useState } from "react";
import { Button, CircularProgress, Divider, TextField } from "@mui/material";
import { IActionPlanTask } from "entities/RCA/ActionPlanEntity/ActionPlanEntityInterfaces";
import { useRCAMainStyles } from "pages/Corporate/RCA/RCA.styles";
import CheckIcon from "@mui/icons-material/Check";

import { useTranslation } from "react-i18next";
import { useStyles } from "./RCAActionPlanTasks.styles";
import RCAActionPlanTaskItem from "../RCAActionPlanTaskItem";
import { ActionPlanEntity } from "entities/RCA/ActionPlanEntity/ActionPlanEntity";
import { IRCAActionPlanTasks } from "./RCAActionPlanTasksInterfaces";
import useRolesPermissions from "hooks/useRolesPermissions";

const RCAActionPlanTasks = ({ actionPlanId, refetchLogs }: IRCAActionPlanTasks) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const mainClasses = useRCAMainStyles();
    const { RCA_PERMISSIONS } = useRolesPermissions();
    const [newTaskDescription, setNewTaskDescription] = useState("");

    const { actionPlanTasks, loading, refetch } = ActionPlanEntity.GetActionPlanTasks(actionPlanId);
    const { insertActionPlanTaskMutation } = ActionPlanEntity.InsertActionPlanTask();
    const { updateActionPlanTaskMutation } = ActionPlanEntity.UpdateActionPlanTask();

    const handleTaskCompletion = async (task: IActionPlanTask) => {
        await updateActionPlanTaskMutation({
            variables: {
                action_plan_task_id: task.action_plan_task_id,
                changes: {
                    completed_at: task.completed_at ? null : new Date(),
                },
            },
        });
        refetch();
        await refetchLogs();
    };

    const handleTaskDeletion = async (task: IActionPlanTask) => {
        await updateActionPlanTaskMutation({
            variables: {
                action_plan_task_id: task.action_plan_task_id,
                changes: {
                    deleted_at: new Date(),
                },
            },
        });
        refetch();
        await refetchLogs();
    };

    const handleTaskAddition = async () => {
        await insertActionPlanTaskMutation({
            variables: {
                action_plan_task: {
                    action_plan_id: actionPlanId,
                    description: newTaskDescription,
                },
            },
        });
        refetch();
        await refetchLogs();
        setNewTaskDescription("");
    };

    if (loading || !actionPlanTasks)
        return (
            <div>
                <CircularProgress color="info" />
            </div>
        );

    return (
        <>
            <div className={classes.inputWrapper}>
                <TextField
                    placeholder={t("rca.action_plans.action_plan_details.tasks.description_placeholder")}
                    aria-labelledby="outlined-newTaskDescription-label"
                    fullWidth
                    disabled={!RCA_PERMISSIONS?.action_plans.canEdit}
                    id="outlined-newTaskDescription-input"
                    className={mainClasses.contentFormTextField}
                    name="actionPlanName"
                    type="text"
                    onKeyDown={async (event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            await handleTaskAddition();
                        }
                    }}
                    variant="outlined"
                    value={newTaskDescription}
                    onChange={(event) => {
                        setNewTaskDescription(event.target.value);
                    }}
                />
                <Button
                    className={mainClasses.contentButton}
                    variant="outlined"
                    color="info"
                    disabled={!newTaskDescription || !RCA_PERMISSIONS?.action_plans.canEdit}
                    onClick={handleTaskAddition}
                >
                    <CheckIcon /> {t("rca.action_plans.action_plan_details.tasks.done")}
                </Button>
            </div>
            <section>
                {actionPlanTasks.map((task) => (
                    <React.Fragment key={task.action_plan_task_id}>
                        <RCAActionPlanTaskItem
                            task={task}
                            onCompleteTask={handleTaskCompletion}
                            onDeleteTask={handleTaskDeletion}
                        />
                        <Divider />
                    </React.Fragment>
                ))}
            </section>
        </>
    );
};

export default RCAActionPlanTasks;
